import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Testimonial.css';

// --- (Helper hooks and functions are unchanged) ---
const chunkArray = (array, size) => {
    const chunkedArr = [];
    if (size <= 0) return [array];
    for (let i = 0; i < array.length; i += size) {
        chunkedArr.push(array.slice(i, i + size));
    }
    return chunkedArr;
};

const useWindowSize = () => {
    const [size, setSize] = useState([window.innerWidth]);
    useEffect(() => {
        const handleResize = () => setSize([window.innerWidth]);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return size;
};

const Testimonial = () => {
    // --- STATE MANAGEMENT ---
    const [showPopup, setShowPopup] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    // **FIX 1 of 3: The state must use 'review'**
    const [formData, setFormData] = useState({
        name: '',
        review: '', // This MUST be 'review'
        rating: 0,
        photo: null
    });

    const [testimonials, setTestimonials] = useState([]);
    const fileInputRef = useRef(null);
    const autoScrollInterval = useRef(null);

    const apiBaseUrl = process.env.REACT_APP_API_BASE || '';

    // --- (API & DATA FETCHING logic is fine) ---
    const fetchReviews = useCallback(async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/api/reviews`);
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            if (data.success && Array.isArray(data.reviews)) {
                const formattedTestimonials = data.reviews.map(review => ({
                    text: review.review,
                    author: review.name,
                    image: review.photo && review.photo.startsWith('http') ? review.photo : `${apiBaseUrl}${review.photo}`,
                    rating: review.rating || 0
                }));
                setTestimonials(formattedTestimonials.reverse());
            }
        } catch (error) {
            console.error("Failed to fetch reviews:", error);
        }
    }, [apiBaseUrl]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    // --- (SLIDER LOGIC is fine) ---
    const [width] = useWindowSize();
    const cardsPerPage = width <= 425 ? 1 : 3;
    const testimonialPages = chunkArray(testimonials, cardsPerPage);
    const pageCount = testimonialPages.length;
    let clonedPages = [];
    if (pageCount > 1) clonedPages = [testimonialPages[pageCount - 1], ...testimonialPages, testimonialPages[0]];
    else if (pageCount === 1) clonedPages = testimonialPages;
    const [currentPage, setCurrentPage] = useState(0);
    const [transitionDuration, setTransitionDuration] = useState('0.5s');
    useEffect(() => { setCurrentPage(pageCount > 1 ? 1 : 0); }, [pageCount]);
    useEffect(() => {
        if (pageCount <= 1 || !clonedPages.length) return;
        const timer = setTimeout(() => {
            if (currentPage === 0 || currentPage === clonedPages.length - 1) {
                setTransitionDuration('0s');
                setCurrentPage(currentPage === 0 ? pageCount : 1);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [currentPage, pageCount, clonedPages.length]);
    useEffect(() => {
        if (transitionDuration === '0s') {
            const timer = setTimeout(() => setTransitionDuration('0.5s'), 50);
            return () => clearTimeout(timer);
        }
    }, [transitionDuration]);
    useEffect(() => {
        const startAutoScroll = () => {
            if (pageCount <= 1) return;
            const stopAutoScroll = () => clearInterval(autoScrollInterval.current);
            stopAutoScroll();
            autoScrollInterval.current = setInterval(() => setCurrentPage(p => p + 1), 4000);
            return stopAutoScroll;
        };
        startAutoScroll();
    }, [pageCount]);
    const handleNavClick = (dir) => {
        if (pageCount <= 1) return;
        setCurrentPage(p => p + (dir === 'next' ? 1 : -1));
    };
    let activeIndicatorIndex = currentPage > 0 ? currentPage - 1 : (pageCount > 1 ? pageCount - 1 : 0);
    if (currentPage === clonedPages.length - 1) activeIndicatorIndex = 0;


    // --- FORM HANDLERS & SUBMISSION ---
    const handleInputChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleRatingClick = (rating) => setFormData(prev => ({ ...prev, rating }));
    const handlePhotoUpload = (e) => {
        if (e.target.files[0]) setFormData(prev => ({ ...prev, photo: e.target.files[0] }));
    };

    const closePopup = () => {
        setShowPopup(false);
        setSubmitMessage('');
        // **FIX 2 of 3: Reset the state using 'review'**
        setFormData({ name: '', review: '', rating: 0, photo: null });
    };

    const handleSubmitReview = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.review || formData.rating === 0) {
            setSubmitMessage('Please fill all required fields and select a rating.');
            return;
        }

        setIsSubmitting(true);
        const submitData = new FormData();

        // **FIX 3 of 3: Append the data using 'review'**
        submitData.append('name', formData.name);
        submitData.append('review', formData.review);
        submitData.append('rating', formData.rating);
        if (formData.photo) {
            submitData.append('photo', formData.photo);
        }

        // --- Crucial Debugging Step ---
        // This will print exactly what you are sending to the server.
        console.log("Submitting the following data:");
        for (let pair of submitData.entries()) {
            console.log(pair[0] + ': ' + pair[1]);
        }
        // --------------------------------

        try {
            const response = await fetch(`${apiBaseUrl}/api/reviews`, {
                method: 'POST',
                body: submitData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Submission failed due to a server error.');
            }

            setSubmitMessage('Thank you! Your review was submitted successfully.');
            await fetchReviews();
            setTimeout(closePopup, 2000);

        } catch (error) {
            console.error('Error submitting review:', error);
            setSubmitMessage(`Error: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="testimonial-section">
                <div className="testimonial-container">
                    {/* <div className="testimonial-header">
                        <h2>Some Of Our Clients <br /> Saying About Us</h2>
                    </div> */}
                    {testimonials.length > 0 ? (
                        <div className="testimonial-slider">
                            <div className="slider-wrapper" style={{ transform: `translateX(-${currentPage * 100}%)`, transition: `transform ${transitionDuration} ease-in-out` }}>
                                {clonedPages.map((page, pageIndex) => (
                                    <div className="slide-page" key={pageIndex}>
                                        {page.map((testimonial, index) => (
                                            <div className="testimonial-card1" key={`${pageIndex}-${index}`}>
                                                <div className="quotes">“</div> <br/>
                                                <img src={testimonial.image} alt={testimonial.author} className="author-image" />
                                                <p className="testimonial-author"><strong>{testimonial.author}</strong></p>

                                                <div className="testimonial-stars">
                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                        <span key={star} className={`star ${star <= testimonial.rating ? 'filled' : ''}`}>★</span>
                                                    ))}
                                                </div>
                                                <p className="testimonial-text">{testimonial.text}</p>

                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p>Loading testimonials...</p>
                    )}
                    {pageCount > 1 && (
                        <div className="slider-controls">
                            <button className="arrow-button prev" onClick={() => handleNavClick('prev')}>&lt;</button>
                            <div className="line-indicators">
                                {testimonialPages.map((_, index) => (
                                    <div key={index} className={`line ${activeIndicatorIndex === index ? 'active' : ''}`} onClick={() => setCurrentPage(index + 1)}></div>
                                ))}
                            </div>
                            <button className="arrow-button next" onClick={() => handleNavClick('next')}>&gt;</button>
                        </div>
                    )}
                    <div className="add-review-container">
                        <button className="add-review-button" onClick={() => setShowPopup(true)}>Add Review</button>
                    </div>
                </div>
            </div>

            {/* --- REVIEW POPUP MODAL --- */}
            <div className={`popup-overlay ${showPopup ? 'active' : ''}`}>
                <div className="popup-modal">
                    <button className="popup-close" onClick={closePopup}>×</button>
                    <h2 className="popup-title">Write A Review</h2>
                    {submitMessage && (
                        <div className={`popup-message ${submitMessage.includes('successfully') ? 'success' : 'error'}`}>
                            {submitMessage}
                        </div>
                    )}
                    <form className="review-form" onSubmit={handleSubmitReview}>
                        <div className="form-group">
                            <label className="form-label">Your Name *</label>
                            <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="form-input" required />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Your Review *</label>
                            <textarea
                                name="review" // This MUST be 'review'
                                value={formData.review} // This MUST be 'review'
                                onChange={handleInputChange}
                                className="form-textarea"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Upload Photo</label>
                            <div className="photo-upload" onClick={() => fileInputRef.current?.click()}>
                                {formData.photo ? <img src={URL.createObjectURL(formData.photo)} alt="Canada PNP" className="photo-preview" /> : <span>Click to upload</span>}
                                <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" style={{ display: 'none' }} />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Rating *</label>
                            <div className="star-rating">
                                {[1, 2, 3, 4, 5].map((star) => <span key={star} className={`star ${star <= formData.rating ? 'active' : ''}`} onClick={() => handleRatingClick(star)}>★</span>)}
                            </div>
                        </div>
                        <div className="form-buttons">
                            <button type="submit" className="btn-submit" disabled={isSubmitting}>{isSubmitting ? 'Submitting...' : 'Submit Review'}</button>
                            <button type="button" className="btn-cancel" onClick={closePopup}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default Testimonial;